import InputField from "@/components/InputField";
import Spinner from "@/components/atoms/Spinner/Spinner";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector } from "@/redux/user/userSlice";
import { clsx } from "clsx";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import SectionList from "./SectionListComponent";

const IncludeExcludeSection = ({
  type,
  icon,
  players,
  showSearch,
  searchTerm,
  setSearchTerm,
  setShowSearch,
  isSearching,
  result,
  onAdd,
  onRemove,
  showAll,
  setShowAll,
  setShowAllSectionType,
  isMobile,
}: {
  type: "include" | "exclude";
  icon: React.ReactNode;
  players: any[];
  showSearch: "include" | "exclude" | undefined;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  setShowSearch: React.Dispatch<
    React.SetStateAction<"include" | "exclude" | undefined>
  >;
  isSearching: boolean;
  result: any[];
  onAdd: (player: any, type: "include" | "exclude") => void;
  onRemove: (type: "include" | "exclude", resourceId: number) => void;
  showAll: boolean;
  setShowAll: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAllSectionType: React.Dispatch<
    React.SetStateAction<"include" | "exclude" | null>
  >;
  isMobile: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);

  const cantIncludePlayers = useMemo(
    () =>
      user?.subscription === "free" &&
      type === "include" &&
      players.length >= 1,
    [user?.subscription, type, players.length],
  );

  const firstTwo = players.slice(0, 2);
  const remaining = players.slice(2);
  const isSearchActive = showSearch === type;

  const resultsBar =
    isSearchActive && !isSearching && result?.length > 0 ? (
      <>
        {isSearching ? <Spinner /> : null}
        <div className="my-2 rounded-lg bg-panel-foreground p-2">
          <span className="text-dark-tertiary text-base">Results:</span>
          <div className="flex flex-wrap gap-2 w-fit mx-auto justify-start items-start p-3">
            {result?.map((p) => (
              <button key={p.resourceId} onClick={() => onAdd(p, type)}>
                <img
                  className="w-32 aspect-[161/225]"
                  src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/playercards/small/${p.resourceId}.png`}
                  alt={p.cardName}
                />
              </button>
            ))}
          </div>
        </div>
      </>
    ) : null;

  return (
    <SectionList.Section
      text={type === "include" ? "Must Include Players" : "Exclude Players"}
      icon={icon}
      searchbar={
        showSearch === type ? (
          <>
            <div>
              <InputField
                type="search"
                autoFocus={true}
                value={searchTerm}
                onChange={setSearchTerm}
                dismissSearch={() => setShowSearch(undefined)}
                placeholder="Search for players"
              />
            </div>
            <div className={"my-2 rounded-lg bg-[#333A42]"}>
              {isSearchActive && isSearching && (
                <div className="flex justify-center">
                  <Spinner />
                </div>
              )}
            </div>
            <div className={"my-2 rounded-lg bg-[#333A42]"}>
              {isSearchActive && !isSearching && result?.length === 0 && (
                <p className="w-full p-4 text-center text-white">
                  {`No players found with the name "${searchTerm}"`}
                </p>
              )}
            </div>
          </>
        ) : null
      }
      searchResults={resultsBar}
    >
      <div className="flex flex-col p-3">
        <div className="flex justify-between">
          <div className={clsx("flex gap-1", isMobile && "gap-5")}>
            {firstTwo.map((player) => (
              <button
                key={player.resourceId}
                onClick={() => onRemove(type, player.resourceId)}
              >
                <img
                  className="h-31 w-22"
                  src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/playercards/small/${player.resourceId}.png`}
                  alt={player.cardName}
                />
              </button>
            ))}
          </div>
          <div className="flex flex-col justify-end min-h-22 pl-1 relative">
            {remaining.length > 0 && (
              <div className="absolute bottom-[42px]">
                <button
                  className="h-[80px] w-[90px] bg-action-tertiary rounded shadow-soft-dark-bottom text-dark-secondary hover:bg-action-tertiary-hover"
                  onClick={() => {
                    if (isMobile) {
                      setShowAllSectionType(type);
                    } else {
                      setShowAll((prev) => !prev);
                    }
                  }}
                >
                  <div className="flex flex-col justify-center items-center">
                    <div className="px-2 py-0.5 bg-[#0576C7] rounded-3xl">{`${players.length}`}</div>
                    <div>Show all</div>
                  </div>
                </button>
              </div>
            )}
            {cantIncludePlayers && (
              <span className="text-xs mb-1 absolute top-0 w-40 text-right right-0 text-dark-secondary">
                You can maximum include 1 player as a free user
              </span>
            )}
            <button
              className="h-9 w-22.5 bg-action-tertiary rounded shadow-soft-dark-bottom text-dark-secondary hover:bg-action-tertiary-hover"
              onClick={(e) => {
                e.stopPropagation();
                if (!user?.uuid) {
                  dispatch(
                    showModal({
                      modalType: "login-required",
                      freeFeatureLoggedInRequiredHook:
                        type === "include"
                          ? "player-inclusion"
                          : "player-exclusion",
                    }),
                  );
                } else if (cantIncludePlayers) {
                  dispatch(
                    showModal({
                      modalType: "premium-feature",
                      freeFeatureLoggedInRequiredHook: "player-inclusion",
                    }),
                  );
                } else {
                  setShowSearch(type);
                }
              }}
            >
              Add +
            </button>
          </div>
        </div>
        {!isMobile && showAll && remaining.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mt-3">
            {remaining.map((player) => (
              <button
                key={player.resourceId}
                onClick={() => onRemove(type, player.resourceId)}
              >
                <img
                  className="h-31 w-22"
                  src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/playercards/small/${player.resourceId}.png`}
                  alt={player.cardName}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </SectionList.Section>
  );
};

export default IncludeExcludeSection;
